/**
 * Did the text read of a PDF do a good job? — the question that decides whether Document
 * Intelligence gets a turn.
 *
 * `useMailExtraction.ts` reads a text PDF locally and only sends the PAGE to DI when that read
 * found nothing. PO_POSXTV-000126-saudi-shipping.pdf (2026-09-21) is the case that rule missed:
 * nine numbered items, each spread over five lines with the quantity four lines below the
 * description, and the prose reader assembled five items out of the wrong numbers — every one
 * wrong — which counted as "found something". DI read the same page as a table and got all nine.
 *
 * The signal is the customer's own numbering: when the text carries a run of line-leading
 * serials (1, 2, 3 …) and the read came back with fewer items than the run is long, the read
 * was poor. Three in a row is the bar — a "1 " and a "2 " can open ordinary sentences.
 */

/** Longest run of consecutive integers opening successive numbered lines. */
export function serialRunLength(text: string): number {
  let best = 0
  let expected = 1
  let run = 0
  for (const line of text.split('\n')) {
    const m = /^\s*(\d{1,3})[.)]?\s+\S/.exec(line)
    if (!m) continue
    const n = Number(m[1])
    if (n === expected) {
      run += 1
      expected += 1
    } else if (n === 1) {
      run = 1
      expected = 2
    } else {
      continue
    }
    if (run > best) best = run
  }
  return best
}

export function pdfTextReadLooksPoor(text: string, itemsRead: number): boolean {
  const run = serialRunLength(text)
  return run >= 3 && itemsRead < run
}

/**
 * The sub-labels a purchase-order form prints under each item — DI folds them into the
 * description cell, and the prose reader drags them along too. Observed labels only; an
 * ordinary description with a colon of its own (`SIZE: L`) is untouched.
 */
const FORM_LABELS = /\s*(?:Position No\.?|Plate\/Drawing No\.?|Account Number|Remarks To Vendor)\s*:.*$/i

export function stripFormLabels(description: string): string {
  return description.replace(FORM_LABELS, '').trim()
}
